import React, { useMemo, useState } from 'react';
import { SectionCard } from '@frontend/app/containers/Projects/CampaignPage/components/SectionCard';
import { Button } from '@frontend/shadcn/components/ui/button';
import { P } from '@frontend/shadcn/components/typography/p';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import { EnvelopeIcon, PlusIcon } from '@revfluence/fresh-icons/regular/esm';
import { TemplateType } from '@frontend/app/types/globalTypes';
import { MessageTemplatesQuery_templates } from '@frontend/app/queries/types/MessageTemplatesQuery';
import { useGetDefaultProgramTemplate, useGetMessageTemplates } from '@frontend/app/hooks';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { Empty } from '@frontend/app/refresh-components';
import { PenIcon } from '@revfluence/fresh-icons/solid/esm';
import { BriefEmailTemplateDrawer } from './BriefEmailTemplateDrawer';
import { TProject } from '../../types';

interface BriefEmailTemplatesProps {
  projectId: number;
  program: TProject;
}

export const BriefEmailTemplates: React.FC<BriefEmailTemplatesProps> = ({ projectId, program }) => {
  const { templates, loading: messageTemplatesLoading, refetch } = useGetMessageTemplates();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplatesQuery_templates | null>(null);

  const briefEmailTemplates = useMemo(() => {
    if (messageTemplatesLoading) return [];
    return templates.filter(
      (template) =>
        template.type === TemplateType.TERMS &&
        template.programId === projectId,
    );
  }, [templates, projectId, messageTemplatesLoading]);

  const {
    data: {
      defaultTemplate: termsTemplate = null,
    } = {},
    loading: termsTemplateLoading,
  } = useGetDefaultProgramTemplate({
    variables: {
      programId: projectId,
    },
    skip: !projectId,
  });

  const loading = messageTemplatesLoading || termsTemplateLoading;

  const handleEditClick = (template: MessageTemplatesQuery_templates) => {
    setSelectedTemplate(template);
    setIsDrawerOpen(true);
  };

  const handleCreateClick = () => {
    setSelectedTemplate(null);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedTemplate(null);
  };

  const handleMutationSuccess = () => {
    refetch();
    handleDrawerClose();
  };

  return (
    <>
      <SectionCard
        title="Brief Email Template"
        description="Create and customize Brief email templates that can be sent with creators' briefs."
        rightContent={
          <Button variant="outline" size="xs" onClick={handleCreateClick} className="self-start mt-0.5">
            <PlusIcon className="mr-1.5" />
            Add Email Template
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          {loading && <Skeleton className="h-10 w-full" />}
          {!loading && briefEmailTemplates.length === 0 && (
            <Empty
              icon={EnvelopeIcon}
              title="No brief email templates found"
              description="Create a brief email template to send with briefs."
              primaryCtaProps={{
                text: 'Create Template',
                onClick: handleCreateClick,
              }}
              layout="horizontal"
              size="lg"
              withBackground
            />
          )}
          {!loading && briefEmailTemplates.map((template) => (
            <Card key={template.id} className="rounded-2xl border border-border">
              <CardContent className="px-4 py-2 flex items-center justify-between">
                <P className="mb-0" fontWeight="font-medium">{template.title}</P>
                <div className="flex items-center gap-2">
                  {termsTemplate?.emailTemplate?.id === template.id && (
                    <Badge variant="secondary">Default</Badge>
                  )}
                  <Button variant="ghost" size="icon" onClick={() => handleEditClick(template)}>
                    <PenIcon />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </SectionCard>

      <BriefEmailTemplateDrawer
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        template={selectedTemplate}
        projectId={projectId}
        program={program}
        onSaveSuccess={handleMutationSuccess}
        onCancel={handleDrawerClose}
        onDeleteSuccess={handleMutationSuccess}
        termsType={termsTemplate?.termsType}
      />
    </>
  );
};

export default BriefEmailTemplates;
